import React, { useEffect, useState } from "react";
import {
  Box,
  FilledInputProps,
  FormControl,
  FormLabel,
  InputBaseProps,
  SxProps,
  InputProps,
  OutlinedInputProps,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import { NumberInput } from "components/NumberInput";
import { InfoTooltip } from "components/InfoTooltip";
import { ITooltipPlacement } from "components/InfoTooltip/InfoTooltip";
import { defineBrowser } from "./constants";
import {
  AUTO_FILL_CANCEL_EVENT_NAME,
  AUTO_FILL_EVENT_NAME,
} from "config/constants";

type Props = {
  formKey: string;
  title: string;
  isRequired?: boolean;
  error?: boolean;
  handleChange?: (value: string) => void;
  numberOnChange?: (value: string) => void;
  numberLiveChange?: (value: string) => void;
  isNumber?: boolean;
  formStyle?: SxProps<Theme>;
  prefix?: string;
  suffix?: string;
  thousandSeparator?: string;
  decimalScale?: number;
  value: string | number;
  labelStyle?: SxProps<Theme>;
  textFieldStyle?: {
    [key: string]: string | number | object;
  };
  placeholder: string;
  maxLength?: number;
  disabled?: boolean;
  inputProps?: InputBaseProps["inputProps"];
  includeTooltip?: boolean;
  tooltipText?: string;
  tooltipPosition?: ITooltipPlacement;
  validateValue?: RegExp | null;
  valueIsNumericString?: boolean;
  allowLeadingZeros?: boolean;
  isAsteriskShown?: boolean;
  errorText?: string | React.ReactNode;
  InputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>;
  multiline?: boolean;
  rows?: number;
};

export const FormField = ({
  formKey,
  title,
  isRequired,
  error,
  handleChange,
  isNumber,
  formStyle,
  value,
  placeholder,
  maxLength,
  labelStyle,
  numberOnChange,
  numberLiveChange,
  textFieldStyle,
  disabled,
  inputProps,
  prefix,
  suffix,
  thousandSeparator,
  decimalScale,
  includeTooltip,
  tooltipText,
  tooltipPosition,
  validateValue,
  valueIsNumericString,
  allowLeadingZeros,
  isAsteriskShown = true,
  errorText,
  InputProps,
  multiline,
  rows,
}: Props) => {
  const theme = useTheme();
  const suffixValue = suffix || "";
  const prefixValue = prefix || "";
  const [textValue, setTextValue] = useState("");

  useEffect(() => {
    if (value !== textValue) {
      setTextValue(String(value));
    }
  }, [value]);

  function handleChangeNumber(value: string) {
    if (numberOnChange) {
      numberOnChange(value);
    }
  }
  return (
    <FormControl variant="outlined" sx={{ ...formStyle }} fullWidth>
      <FormLabel
        htmlFor={formKey}
        sx={{ ...labelStyle, display: "flex", color: "primary.main" }}
      >
        {title}
        {isRequired && isAsteriskShown && (
          <Box
            component={"span"}
            sx={{ color: theme.palette.secondary.main, ml: 1 }}
          >
            *
          </Box>
        )}
        {includeTooltip && (
          <InfoTooltip title={tooltipText} placement={tooltipPosition} />
        )}
      </FormLabel>
      {isNumber ? (
        <NumberInput
          value={value}
          error={error}
          name={formKey}
          prefix={prefix}
          onChange={handleChangeNumber}
          onValueChangeLive={numberLiveChange}
          suffix={suffix}
          thousandSeparator={thousandSeparator}
          InputStyle={{
            fontWeight: 300,
            backgroundColor: disabled ? "#F9F9F9" : "transparent",
            ...textFieldStyle,
          }}
          disabled={disabled}
          decimalScale={decimalScale}
          maxLength={maxLength}
          placeholder={placeholder}
          validateValue={validateValue}
          valueIsNumericString={valueIsNumericString}
          allowLeadingZeros={allowLeadingZeros}
        />
      ) : (
        <TextField
          name={formKey}
          id={formKey}
          value={textValue}
          error={error}
          multiline={multiline}
          rows={rows}
          helperText={error ? errorText : ""}
          FormHelperTextProps={{
            sx: {
              position: "absolute",
              bottom: "-20px",
              mt: 0,
              fontSize: "12px",
            },
          }}
          onBlur={() => handleChange && handleChange(textValue)}
          onChange={(event) => {
            const animationName = (
              (
                event.target as HTMLInputElement
              ).getAnimations()[0] as AnimationEventInit
            )?.animationName;
            if (
              animationName === AUTO_FILL_EVENT_NAME ||
              animationName === AUTO_FILL_CANCEL_EVENT_NAME
            ) {
              handleChange?.(event.target.value);
            }

            setTextValue(`${prefixValue}${event.target.value}${suffixValue}`);
          }}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{
            ...inputProps,
            autoComplete: defineBrowser(),
            maxLength: maxLength,
          }}
          InputProps={{ ...InputProps }}
          sx={{
            color: disabled ? "#BFBFBF" : "primary.main",
            backgroundColor: disabled ? "#F9F9F9" : "transparent",

            "& .MuiInputBase-root.MuiOutlinedInput-root": {
              color: error ? "error.main" : "auto",
            },

            "& .MuiOutlinedInput-input": {
              padding: "12px",
              "::placeholder": {
                color: "primary.main",
                opacity: "0.6",
              },
            },

            "& .MuiFormHelperText-root": {
              fontSize: "12px",
            },

            "& .Mui-disabled": {
              WebkitTextFillColor: "#BFBFBF",
            },
            ...textFieldStyle,
          }}
        />
      )}
    </FormControl>
  );
};
